/**
 * AuthManager - Singleton Pattern for Centralized Firebase Auth State Management
 * 
 * Solves the race condition issue with multiple Firebase Auth listeners
 * by centralizing authentication state in a single instance
 */

import { User, onAuthStateChanged, Auth } from '@/lib/firebase-stubs/firebase-auth';
import { getClientAuth } from '../firebase-client';

export interface AuthState {
  user: User | null;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;
  lastUpdate: number;
}

export interface AuthListener {
  id: string;
  callback: (state: AuthState) => void;
}

class AuthManager {
  private static instance: AuthManager | null = null;
  private auth: Auth | null = null;
  private state: AuthState = {
    user: null,
    isLoading: true,
    isInitialized: false,
    error: null,
    lastUpdate: Date.now()
  };
  
  private listeners: Map<string, AuthListener> = new Map();
  private firebaseUnsubscribe: (() => void) | null = null;
  private initializationPromise: Promise<void> | null = null;
  private debugEnabled = process.env.NODE_ENV === 'development';

  private constructor() {
    if (this.debugEnabled) {
      console.log('[AuthManager] Creating singleton instance');
    }
  }

  public static getInstance(): AuthManager {
    if (!AuthManager.instance) {
      AuthManager.instance = new AuthManager();
    }
    return AuthManager.instance;
  }

  private log(message: string, data?: any) {
    if (this.debugEnabled) {
      console.log(`[AuthManager] ${message}`, data || '');
    }
  }

  private updateState(updates: Partial<AuthState>) {
    const oldState = { ...this.state };
    this.state = {
      ...this.state,
      ...updates,
      lastUpdate: Date.now()
    };
    
    this.log('State updated', { 
      oldState, 
      newState: this.state,
      listeners: this.listeners.size 
    });
    
    this.notifyListeners();
  }

  private notifyListeners() {
    this.listeners.forEach((listener, id) => {
      try {
        listener.callback(this.state);
      } catch (error) {
        console.error(`[AuthManager] Error notifying listener ${id}:`, error);
      }
    });
  }

  public async initialize(): Promise<void> {
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = this._initialize();
    return this.initializationPromise;
  }

  private async _initialize(): Promise<void> {
    try {
      this.log('Initializing AuthManager');
      
      // Skip initialization on server side
      if (typeof window === 'undefined') {
        this.log('Server-side environment, skipping initialization');
        this.updateState({
          isLoading: false,
          isInitialized: true
        });
        return;
      }

      this.auth = getClientAuth();
      
      if (!this.auth) {
        throw new Error('Firebase Auth not available');
      }

      // IMPORTANT: Try auto-sign-in BEFORE setting up the listener
      // This prevents the race condition where onAuthStateChanged fires with null
      // before we have a chance to restore the session
      const storedToken = localStorage.getItem('firebaseToken');
      if (storedToken && !this.auth.currentUser) {
        this.log('Found stored token, attempting auto-sign-in...');
        try {
          const { signInWithCustomToken } = await import('firebase/auth');
          await signInWithCustomToken(this.auth, storedToken);
          this.log('Auto-sign-in successful');
        } catch (error) {
          this.log('Auto-sign-in failed, clearing invalid token');
          localStorage.removeItem('firebaseToken');
        }
      }

      this.log('Setting up Firebase Auth listener');
      
      // Set up the SINGLE Firebase Auth listener
      this.firebaseUnsubscribe = onAuthStateChanged(
        this.auth,
        (user) => {
          this.log('Firebase Auth state changed', {
            hasUser: !!user,
            uid: user?.uid,
            email: user?.email
          });
          
          this.updateState({
            user,
            isLoading: false,
            isInitialized: true,
            error: null
          });
        },
        (error) => {
          console.error('[AuthManager] Firebase Auth error:', error);
          this.updateState({
            user: null,
            isLoading: false,
            isInitialized: true,
            error: error.message
          });
        }
      );

      this.log('AuthManager initialized successfully');
      
    } catch (error) {
      console.error('[AuthManager] Initialization failed:', error);
      this.updateState({
        user: null,
        isLoading: false,
        isInitialized: true,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    }
  }

  public addListener(id: string, callback: (state: AuthState) => void): () => void {
    this.log(`Adding listener: ${id}`);
    
    const listener: AuthListener = { id, callback };
    this.listeners.set(id, listener);
    
    // Immediately call with current state
    callback(this.state);
    
    // Return cleanup function
    return () => {
      this.log(`Removing listener: ${id}`);
      this.listeners.delete(id);
    };
  }

  public getState(): AuthState {
    return { ...this.state };
  }

  public getCurrentUser(): User | null {
    return this.state.user;
  }

  public isUserAuthenticated(): boolean {
    return this.state.user !== null && this.state.isInitialized;
  }

  public isReady(): boolean {
    return this.state.isInitialized && !this.state.isLoading;
  }

  public getDebugInfo() {
    return {
      state: this.state,
      listenersCount: this.listeners.size,
      hasFirebaseListener: !!this.firebaseUnsubscribe,
      isInitialized: !!this.initializationPromise
    };
  }

  public async destroy(): Promise<void> {
    this.log('Destroying AuthManager');
    
    if (this.firebaseUnsubscribe) {
      this.firebaseUnsubscribe();
      this.firebaseUnsubscribe = null;
    }
    
    this.listeners.clear();
    this.auth = null;
    this.initializationPromise = null;
    
    this.updateState({
      user: null,
      isLoading: true,
      isInitialized: false,
      error: null
    });
  }

  // Static method for cleanup during tests
  public static destroyInstance(): void {
    if (AuthManager.instance) {
      AuthManager.instance.destroy();
      AuthManager.instance = null;
    }
  }
}

export default AuthManager;